/**
 * switchAndSave.ts — which question carries the Switch & Save panel, and every
 * word the panel says (CTO, 2026-09-11).
 *
 * Owner repo: @thegpservice/gpsclinics-portal.
 *
 * WHAT IT IS. When a patient reaches the question asking whether they have used
 * weight-loss treatment before (the one a switcher answers "yes" to), the space
 * under the answers tells them what they get if they move to us: our typical
 * saving, a weight-loss plan, tips, diet plans and, near the end of the plan, an
 * introduction to our longevity partner. It is copy. It names no other provider
 * and quotes nobody's price. The CTO was explicit: "this is not about comparing
 * with other services or giving a price".
 *
 * WHERE, AND WHY IT IS SAFE THERE. clinic-service serves that question only after
 * the eligibility gate (`surface: 'assessment'`, because its options name
 * prescription-only medicines), so a saving shown beside it is never on a public
 * surface (R28). The panel sits BELOW the answers: the question is clinical and
 * is answered first, in reading order as well as on screen.
 *
 * ⚠️ AND, SINCE 2026-09-25, ON THE JOURNEY ENTRY LOADER (CTO request). Its content
 * card (`entryLoaderContent.ts`) shows {@link SWITCH_SAVE_NAME} and, since the
 * icon-led card of the same evening, the SHORT form {@link SWITCH_SAVE_SHORT}
 * (the lead's own claim, qualifier and comparator, without the rest of the
 * sentence) to every fresh start — before any gate. The words name no medicine
 * and pass R28 and the Art 9 list; what made the panel post-gate was the
 * QUESTION beside it, whose options name POMs, and that has not moved. The CAP
 * 3.7 point below now covers the loader too. ⚠️ Never "Switching? …" there: the
 * word is on the Art 9 `HEALTH_SHAPED` list (treatment-status phrasing).
 *
 * HOW THE QUESTION IS FOUND. By its answer vocabulary, never its key, the same way
 * `planPrices.ts` finds the plan-preference question: it is the only single-select
 * in the set offering both a first-time answer (`none`) and an "another treatment
 * / not sure" answer (`other_not_sure`). If clinic-service renames either key the
 * panel stops showing, which is the safe way for a promotion to fail, and
 * `switchAndSave.spec.tsx` names the two keys it relies on.
 *
 * ⚠️ THE 8–10% IS A COMPARATIVE PRICE CLAIM, AND ITS EVIDENCE IS NOT HELD. The
 * CAP Code (rule 3.7) expects substantiation to exist before the claim is
 * published and for as long as it runs. prices #65 built `market-benchmarks.ts`
 * to hold exactly that, explicitly "OFF until figures exist", and prices #66
 * reverted it — so there is no benchmark data anywhere in the estate and this
 * number is a constant with nothing behind it. The CTO set the figure at 8–10%
 * on 2026-09-13 (it shipped as 8–12%); correcting the number does NOT discharge
 * the obligation, and the claim stays exposed until a dated basket of named
 * competitors' published prices for equivalent plans is held and refreshed.
 * The range lives in ONE constant so a change of evidence is a one-line change.
 */
import type { QuestionPublic } from '../api/types';

/**
 * The panel's name — its heading (`SwitchAndSave.tsx` draws it with a tinted
 * ampersand) and the bold name that opens the entry loader card's Switch & Save
 * chip (2026-09-25).
 */
export const SWITCH_SAVE_NAME = 'Switch & Save';

/** Our typical saving against other UK online weight-loss clinics (CTO, 2026-09-13). */
export const SWITCH_SAVE_RANGE = { min: 8, max: 10 } as const;

export const SWITCH_SAVE_LEAD = `Already with another clinic? Our plans typically cost ${SWITCH_SAVE_RANGE.min}–${SWITCH_SAVE_RANGE.max}% less than other UK online weight-loss clinics, and every plan comes with more than your treatment.`;

/**
 * SHORT FORM of {@link SWITCH_SAVE_LEAD} — the entry loader card's Switch & Save
 * chip, run in after {@link SWITCH_SAVE_NAME} and an em dash ("Switch & Save —
 * typically 8–10% less than …"; 2026-09-25, CTO: "too much text … catchy"). It
 * is the lead's own claim with the subject ("our plans") carried by the name:
 * the SAME figure from {@link SWITCH_SAVE_RANGE}, the SAME qualifier
 * ("typically" — dropping it would claim every switcher saves that much) and
 * the SAME comparator ("than other UK online weight-loss clinics" — dropping it
 * would leave "less than what?", and a price comparison must say what it is
 * compared with). `entryLoaderContent.spec` pins that the lead still contains
 * this tail word for word.
 */
export const SWITCH_SAVE_SHORT = `typically ${SWITCH_SAVE_RANGE.min}–${SWITCH_SAVE_RANGE.max}% less than other UK online weight-loss clinics`;

export const SWITCH_SAVE_BENEFITS_INTRO = 'When you switch to us, you also get:';

/**
 * The last line is an OFFER. Passing a patient to a partner shares their details,
 * and health details need their explicit say-so (UK GDPR Art. 9), so the copy
 * promises an introduction they can take up, never one that simply happens.
 */
export const SWITCH_SAVE_BENEFITS: readonly string[] = [
  'A weight-loss plan to guide you through your treatment',
  'Weight-loss tips to help you keep going',
  'Diet plans with simple, healthy meals',
  'Near the end of your plan, an introduction to GeneFuel, our longevity partner, if you’d like one',
];

const FIRST_TIME_KEY = 'none';
const NOT_SURE_KEY = 'other_not_sure';

/** True for the prior-treatment question: the screen a switcher answers. */
export function isPriorTreatmentQuestion(question: QuestionPublic): boolean {
  if (question.kind !== 'single_select') return false;
  const keys = new Set((question.options ?? []).map((option) => option.key));
  return keys.has(FIRST_TIME_KEY) && keys.has(NOT_SURE_KEY);
}
